import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const milestones = [
  { year: '2016', event: '心猫咨询创立，第一代心理咨询互联网服务平台上线', color: '#c4544a' },
  { year: '2019', event: '在杭州成立「一尚」，探索科技融合；疫情期间服务10万+人次在线咨询', color: '#e0883a' },
  { year: '2021', event: '走入职场、商会、企业家、高校、职校场景心理健康服务方案', color: '#d4a830' },
  { year: '2022', event: '与上海头部产业园合作；互联网医疗服务商；AI人工智能公司合作', color: '#3aaa6a' },
  { year: '2025', event: '成立乐恩心理事务所；职教心理健康学习中心；AI融合学习中心', color: '#2a9ab8' },
  { year: '2026', event: '启动「专业+AI双驱动」战略，立志成为中国高品质社会心理健康服务运营平台', color: '#8a5ab8' },
];

const stats = [
  { num: '10', unit: '年+', label: '深耕心理健康服务' },
  { num: '10', unit: '万+', label: '疫情期间在线咨询人次' },
  { num: '316', unit: '位', label: '链接全球顶级心理专家' },
  { num: '1', unit: '万+', label: '心理爱好者与咨询师' },
];

export default function BrandHistorySection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(logoRef.current, {
        y: 30,
        opacity: 0,
        duration: 1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: logoRef.current,
          start: 'top bottom-=10%',
          toggleActions: 'play none none reverse',
        },
      });

      if (statsRef.current) {
        gsap.from(statsRef.current.querySelectorAll('.stat-item'), {
          y: 20,
          opacity: 0,
          duration: 0.6,
          stagger: 0.1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: statsRef.current,
            start: 'top bottom-=10%',
            toggleActions: 'play none none reverse',
          },
        });
      }

      if (timelineRef.current) {
        gsap.from(timelineRef.current.querySelectorAll('.milestone-item'), {
          x: -20,
          opacity: 0,
          duration: 0.6,
          stagger: 0.12,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: timelineRef.current,
            start: 'top bottom-=10%',
            toggleActions: 'play none none reverse',
          },
        });
      }
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="brand-history"
      ref={sectionRef}
      style={{
        backgroundColor: '#fcfcfc',
        padding: '10vmax 5vmax',
      }}
    >
      {/* Top divider */}
      <div
        style={{
          height: '1px',
          backgroundColor: 'rgba(21, 20, 20, 0.08)',
          marginTop: '-10vmax',
          marginLeft: '-5vmax',
          marginRight: '-5vmax',
          marginBottom: '10vmax',
        }}
      />

      <div className="max-w-6xl mx-auto">
        {/* Honor Wall - 荣誉资质 */}
        <div id="honor-wall" className="mb-20">
          {/* Section header */}
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div style={{ width: '24px', height: '1px', backgroundColor: 'rgba(200, 170, 130, 0.3)' }} />
              <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: 'rgba(184, 152, 80, 0.5)' }} />
              <div style={{ width: '24px', height: '1px', backgroundColor: 'rgba(200, 170, 130, 0.3)' }} />
            </div>
            <h3
              className="font-serif"
              style={{
                fontSize: 'clamp(1.3rem, 2.5vw, 1.8rem)',
                fontWeight: 400,
                color: '#9a7a50',
                letterSpacing: '0.15em',
                textShadow: '1px 1px 0 rgba(255,255,255,0.5)',
              }}
            >
              荣誉资质
            </h3>
            <p
              className="mt-2"
              style={{ fontSize: '0.78rem', color: 'rgba(110, 94, 80, 0.45)', letterSpacing: '0.08em' }}
            >
              HONORS & CERTIFICATES
            </p>
          </div>

          {/* Gallery wall */}
          <div
            className="relative py-10 px-6 lg:px-10"
            style={{
              backgroundColor: '#f7f3ec',
              borderRadius: '2px',
              border: '1px solid rgba(200, 170, 130, 0.12)',
            }}
          >
            {/* Decorative corner marks */}
            <div className="absolute top-4 left-4" style={{ width: '20px', height: '20px', borderTop: '1px solid rgba(184, 152, 80, 0.2)', borderLeft: '1px solid rgba(184, 152, 80, 0.2)' }} />
            <div className="absolute top-4 right-4" style={{ width: '20px', height: '20px', borderTop: '1px solid rgba(184, 152, 80, 0.2)', borderRight: '1px solid rgba(184, 152, 80, 0.2)' }} />
            <div className="absolute bottom-4 left-4" style={{ width: '20px', height: '20px', borderBottom: '1px solid rgba(184, 152, 80, 0.2)', borderLeft: '1px solid rgba(184, 152, 80, 0.2)' }} />
            <div className="absolute bottom-4 right-4" style={{ width: '20px', height: '20px', borderBottom: '1px solid rgba(184, 152, 80, 0.2)', borderRight: '1px solid rgba(184, 152, 80, 0.2)' }} />

            {/* Horizontal awards - filmstrip style */}
            <div className="mb-8">
              <p
                className="text-center mb-5"
                style={{ fontSize: '0.7rem', color: 'rgba(154, 122, 80, 0.5)', letterSpacing: '0.2em', textTransform: 'uppercase' }}
              >
                资质认定 · 行业认证
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
                {[
                  { src: '/images/honor-01.jpg', alt: '创新型中小企业认定' },
                  { src: '/images/honor-02.jpg', alt: '临床心理工作委员会认证' },
                  { src: '/images/honor-03.jpg', alt: '软件著作权登记证书' },
                  { src: '/images/honor-04.jpg', alt: '养老协会合作资质' },
                  { src: '/images/honor-05.jpg', alt: '2023年度优秀组织奖' },
                  { src: '/images/honor-06.jpg', alt: '2024年度优秀组织奖' },
                  { src: '/images/honor-07.jpg', alt: '职场职教学习会合作' },
                ].map((h, i) => (
                  <div
                    key={i}
                    className="interactive group"
                    style={{ perspective: '600px' }}
                  >
                    <div
                      className="relative overflow-hidden transition-all duration-500"
                      style={{
                        borderRadius: '2px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(200, 170, 130, 0.15)',
                        transformStyle: 'preserve-3d',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px)';
                        e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.1), 0 0 0 1px rgba(184, 152, 80, 0.25)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(200, 170, 130, 0.15)';
                      }}
                    >
                      <div style={{ aspectRatio: '3/2', backgroundColor: '#faf8f4' }}>
                        <img
                          src={h.src}
                          alt={h.alt}
                          className="w-full h-full object-contain"
                          loading="lazy"
                        />
                      </div>
                    </div>
                    <p
                      className="text-center mt-2 transition-opacity duration-300 opacity-0 group-hover:opacity-100"
                      style={{ fontSize: '0.65rem', color: 'rgba(110, 94, 80, 0.5)', lineHeight: 1.4 }}
                    >
                      {h.alt}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="flex items-center justify-center gap-3 my-6">
              <div style={{ width: '40px', height: '1px', backgroundColor: 'rgba(200, 170, 130, 0.2)' }} />
              <div style={{ width: '3px', height: '3px', borderRadius: '50%', backgroundColor: 'rgba(184, 152, 80, 0.3)' }} />
              <div style={{ width: '40px', height: '1px', backgroundColor: 'rgba(200, 170, 130, 0.2)' }} />
            </div>

            {/* Vertical certificates - portrait gallery */}
            <div>
              <p
                className="text-center mb-5"
                style={{ fontSize: '0.7rem', color: 'rgba(154, 122, 80, 0.5)', letterSpacing: '0.2em', textTransform: 'uppercase' }}
              >
                合作基地 · 学术共建
              </p>
              <div className="flex justify-center gap-6 lg:gap-10">
                {[
                  { src: '/images/honor-v1.jpg', alt: '中华志愿者协会会员单位' },
                  { src: '/images/honor-v2.jpg', alt: '中华志愿者协会实践基地' },
                  { src: '/images/honor-v3.jpg', alt: '中国科学院上海分院合作' },
                ].map((h, i) => (
                  <div
                    key={i}
                    className="interactive group"
                    style={{
                      width: 'clamp(130px, 18vw, 200px)',
                      perspective: '600px',
                    }}
                  >
                    <div
                      className="relative overflow-hidden transition-all duration-500"
                      style={{
                        borderRadius: '2px',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.06), 0 0 0 1px rgba(200, 170, 130, 0.15)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-6px)';
                        e.currentTarget.style.boxShadow = '0 16px 40px rgba(0,0,0,0.1), 0 0 0 1px rgba(184, 152, 80, 0.25)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06), 0 0 0 1px rgba(200, 170, 130, 0.15)';
                      }}
                    >
                      <img
                        src={h.src}
                        alt={h.alt}
                        className="w-full object-contain"
                        style={{ display: 'block' }}
                        loading="lazy"
                      />
                    </div>
                    <p
                      className="text-center mt-2.5 transition-opacity duration-300 opacity-0 group-hover:opacity-100"
                      style={{ fontSize: '0.65rem', color: 'rgba(110, 94, 80, 0.5)', lineHeight: 1.4 }}
                    >
                      {h.alt}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Caption */}
          <p
            className="text-center mt-6"
            style={{ fontSize: '0.72rem', color: 'rgba(110, 94, 80, 0.35)', letterSpacing: '0.08em' }}
          >
            部分荣誉资质展示，持续更新中
          </p>
        </div>

        {/* Divider */}
        <div style={{ height: '1px', backgroundColor: 'rgba(21, 20, 20, 0.08)', marginBottom: '5vmax' }} />

        {/* Brand Identity */}
        <div ref={logoRef} className="mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-0">
            {/* Left: Logo mark + name */}
            <div className="lg:col-span-5 flex flex-col items-start">
              <div className="flex items-center gap-4 mb-6">
                {/* SVG Logo */}
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 64 64"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="32" cy="32" r="30" stroke="#151414" strokeWidth="1.5" fill="none" />
                  <circle cx="22" cy="24" r="6" stroke="#151414" strokeWidth="1" fill="none" opacity="0.6" />
                  <circle cx="40" cy="28" r="4" stroke="#151414" strokeWidth="1" fill="none" opacity="0.4" />
                  <circle cx="28" cy="42" r="5" stroke="#151414" strokeWidth="1" fill="none" opacity="0.5" />
                  <circle cx="44" cy="40" r="3" stroke="#151414" strokeWidth="1" fill="none" opacity="0.3" />
                  <path d="M22 24L28 42" stroke="#151414" strokeWidth="0.5" opacity="0.3" />
                  <path d="M40 28L44 40" stroke="#151414" strokeWidth="0.5" opacity="0.3" />
                  <path d="M22 24L40 28" stroke="#151414" strokeWidth="0.5" opacity="0.3" />
                </svg>

                <div>
                  <div className="flex items-baseline gap-2">
                    <span
                      className="font-display"
                      style={{
                        fontSize: '1.25rem',
                        fontWeight: 400,
                        letterSpacing: '0.02em',
                        color: '#9a6850',
                      }}
                    >
                      孔隙心理
                    </span>
                    <span style={{ color: 'rgba(21, 20, 20, 0.2)', fontSize: '0.9rem' }}>×</span>
                    <span
                      className="font-display"
                      style={{
                        fontSize: '1rem',
                        fontWeight: 400,
                        letterSpacing: '0.02em',
                        color: '#6e5e50',
                      }}
                    >
                      有心AI
                    </span>
                  </div>
                  <p
                    style={{
                      fontSize: '0.7rem',
                      letterSpacing: '0.12em',
                      color: '#6e5e50',
                      textTransform: 'uppercase',
                      marginTop: '2px',
                    }}
                  >
                    POROSITY PSYCHOLOGY × YOUXIN AI
                  </p>
                </div>
              </div>

              <p
                style={{
                  fontSize: '0.92rem',
                  color: '#6e5e50',
                  lineHeight: 1.6,
                  letterSpacing: '0.05em',
                }}
              >
                社会心理健康服务生态资源运营平台
              </p>
            </div>

            {/* Vertical divider */}
            <div
              className="hidden lg:block lg:col-span-1"
              style={{
                borderLeft: '1px solid rgba(21, 20, 20, 0.08)',
              }}
            />

            {/* Right: Concept */}
            <div id="brand-concept" className="lg:col-span-6 lg:pl-8">
              {/* Subtle warm accent dot */}
              <div
                className="mb-4"
                style={{
                  width: '5px',
                  height: '5px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(200, 170, 130, 0.35)',
                }}
              />
              <p
                className="text-caption mb-4"
                style={{ color: '#6e5e50' }}
              >
                品牌理念
              </p>
              <p
                className="font-serif mb-6"
                style={{
                  fontSize: 'clamp(1.1rem, 2vw, 1.5rem)',
                  fontWeight: 400,
                  lineHeight: 1.4,
                  color: '#7a6a58',
                  textShadow: '1px 1px 0 rgba(255,255,255,0.5)',
                }}
              >
                「孔隙」取自材料科学——那些看似空无一物的微小空间，恰恰是物质得以呼吸、能量得以流动、生命得以渗透的关键所在
              </p>
              <p
                style={{
                  fontSize: '0.9rem',
                  lineHeight: 1.7,
                  color: '#6e5e50',
                }}
              >
                正如陶土中的孔隙让器物在窑火中不爆裂、能呼吸，人的内心也需要这样的「心理孔隙」——允许情绪流动、允许脆弱存在、允许改变发生的空间。我们致力于在每一个组织中创造这样的心理孔隙，让心理健康服务像空气一样自然渗透。
              </p>
            </div>
          </div>

          {/* Formula */}
          <div
            className="mt-10 pt-6"
            style={{ borderTop: '1px solid rgba(21, 20, 20, 0.08)' }}
          >
            <p
              className="font-display"
              style={{
                fontSize: 'clamp(0.8rem, 1.3vw, 1rem)',
                fontWeight: 400,
                color: '#151414',
                letterSpacing: '-0.01em',
              }}
            >
              主动心理健康 =（科学|专业 + 与时俱进 + 人文关怀）× AI
            </p>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: '1px', backgroundColor: 'rgba(21, 20, 20, 0.08)', marginBottom: '5vmax' }} />

        {/* History */}
        <div id="company-history">
          <div className="flex items-baseline justify-between mb-10">
            <div>
              <p className="text-caption mb-2" style={{ color: '#6e5e50' }}>发展历程</p>
              <p
                className="font-serif"
                style={{
                  fontSize: 'clamp(1.1rem, 2vw, 1.5rem)',
                  fontWeight: 400,
                  color: '#9a6850',
                  textShadow: '1px 1px 0 rgba(255,255,255,0.5), 2px 2px 0 rgba(180,150,130,0.15)',
                }}
              >
                十年深耕，一个愿景
              </p>
            </div>
            <p
              className="hidden sm:block"
              style={{
                fontSize: '0.78rem',
                color: '#6e5e50',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              2016 — 2026
            </p>
          </div>

          {/* Stats */}
          <div
            ref={statsRef}
            className="grid grid-cols-4 gap-0 mb-12"
          >
            {stats.map((stat, i) => (
              <div
                key={i}
                className="stat-item py-4"
                style={{
                  borderLeft: i > 0 ? '1px solid rgba(21, 20, 20, 0.08)' : 'none',
                }}
              >
                <div className="flex items-baseline gap-1 justify-center">
                  <span
                    className="font-display"
                    style={{
                      fontSize: 'clamp(1.5rem, 3vw, 2.5rem)',
                      fontWeight: 300,
                      lineHeight: 1,
                      color: '#151414',
                    }}
                  >
                    {stat.num}
                  </span>
                  <span
                    className="font-display"
                    style={{
                      fontSize: '0.9rem',
                      fontWeight: 400,
                      color: '#6e5e50',
                    }}
                  >
                    {stat.unit}
                  </span>
                </div>
                <p
                  className="text-center mt-2"
                  style={{
                    fontSize: '0.75rem',
                    color: '#6e5e50',
                    letterSpacing: '0.05em',
                  }}
                >
                  {stat.label}
                </p>
              </div>
            ))}
          </div>

          {/* Timeline - Exact UI match */}
          <div ref={timelineRef} className="relative">
            {/* Single faint vertical line */}
            <div
              className="absolute top-0 bottom-0"
              style={{
                left: '4.5rem',
                width: '1px',
                backgroundColor: '#e8e6e2',
              }}
            />

            <div className="space-y-0">
              {milestones.map((m, i) => (
                <div
                  key={m.year}
                  className="milestone-item relative flex items-start py-5"
                  style={{
                    borderBottom: i < milestones.length - 1 ? '1px solid #f0eeea' : 'none',
                  }}
                >
                  {/* Year — colored */}
                  <span
                    className="font-body flex-shrink-0"
                    style={{
                      width: '3.8rem',
                      fontSize: '0.92rem',
                      fontWeight: 500,
                      color: m.color,
                      letterSpacing: '0.01em',
                      paddingTop: '1px',
                    }}
                  >
                    {m.year}
                  </span>

                  {/* Small dot — same color */}
                  <div
                    className="flex-shrink-0 relative"
                    style={{
                      width: '1.4rem',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        left: '50%',
                        top: '0.45rem',
                        transform: 'translateX(-50%)',
                        width: '5px',
                        height: '5px',
                        borderRadius: '50%',
                        backgroundColor: m.color,
                        opacity: i === milestones.length - 1 ? 1 : 0.65,
                      }}
                    />
                  </div>

                  {/* Event — same color, slightly softer */}
                  <p
                    className="font-body"
                    style={{
                      fontSize: '0.92rem',
                      lineHeight: 1.5,
                      color: m.color,
                      fontWeight: 400,
                      paddingTop: '1px',
                    }}
                  >
                    {m.event}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Vision */}
          <div
            className="mt-10 pt-6 flex items-start gap-4"
            style={{ borderTop: '1px solid rgba(21, 20, 20, 0.08)' }}
          >
            <span style={{ fontSize: '0.92rem', color: '#6e5e50', lineHeight: 1.6 }}>→</span>
            <p
              className="font-serif"
              style={{
                fontSize: '0.85rem',
                fontWeight: 400,
                lineHeight: 1.5,
                color: '#151414',
              }}
            >
              成为高品质、立足本土、并肩国际专业水平的社会心理服务生态平台
            </p>
          </div>
        </div>
      </div>

      {/* Bottom divider */}
      <div
        style={{
          height: '1px',
          backgroundColor: 'rgba(21, 20, 20, 0.08)',
          marginBottom: '-10vmax',
          marginLeft: '-5vmax',
          marginRight: '-5vmax',
          marginTop: '10vmax',
        }}
      />
    </section>
  );
}
